import { LitElement, html, TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { GzCountDownEvents } from '../index';
import { CountdownTranslations } from '../types';

export class GzCountDownWebComponent extends LitElement {
  // see https://lit-element.polymer-project.org/guide/properties
  @property({ type: Number, attribute: true, reflect: true })
  dateToTimestamp = 0;

  /**
   *  Milestones should be provided in seconds
   *  When timer reached that number in seconds a custom event will be triggered
   */
  @property({ type: Array, attribute: true, reflect: true })
  milestones: Array<number> = [];

  @property({ type: Boolean, reflect: true })
  calendarStyle = false;

  @property({type: Boolean, reflect: true})
  inStandBy = false;

  @property({ type: Object })
  translations: CountdownTranslations = {
    titleMore: 'Time left:',
    titleLess: 'In less than:',
    day: 'Day',
    days: 'Days',
  };

  @property({type: String})
  standByTranslation = '';

  @property({type: String})
  standByTooltip = '';

  @state()
  private days = '0';

  @state()
  private hours = '0';

  @state()
  private minutes = '0';

  @state()
  private seconds = '0';

  @state()
  private interval: ReturnType<typeof setTimeout>;

  @state()
  private startedTimer = false;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  render() {
    if (this.inStandBy) {
      return this.renderStandBy();
    }
    return html` ${this.calendarStyle ? this.renderCalendarStyle() : this.renderTimer()} `;
  }

  private renderStandBy(): TemplateResult | undefined {
    return html`
      <gz-tooltip tooltipText="${this.standByTooltip}">
        <div class="calendar-style d-flex flex-column align-items-center justify-center">
          <gz-icon
            name="icon-pending_sandbox"
            size="32"
            color="var(--grey)"
          ></gz-icon>
          <gz-text class="stand-by-text">${this.standByTranslation}</gz-text>
        </div>
      </gz-tooltip>
    `;
  }

  private renderCalendarStyle(): TemplateResult | undefined {
    return html`
      <div class="calendar-style d-flex flex-column align-items-center justify-center">
        <gz-text class="title pb-1" fontSize="10" fontWeight="500"
          >${this.startedTimer ? this.renderTitleText() : ''}</gz-text
        >
        <div class="calendar-container d-flex flex-column">${this.renderCalendarTimer()}</div>
      </div>
    `;
  }

  private renderTimer(): TemplateResult | undefined {
    return html`
      <div class="timer-content">
        <div class="timer">
          ${this.renderDays()} ${this.renderHours()}
          <div id="mins">${this.minutes}:</div>
          <div id="secs">${this.seconds} mins</div>
        </div>
      </div>
    `;
  }

  private renderCalendarTimer() {
    if (!this.startedTimer) {
      return html`<gz-progress-spinner class="button-spinner d-flex justify-center" spinnerwidth="medium">
      </gz-progress-spinner>`;
    }

    return html`<gz-text>${this.renderDaysElement()}</gz-text>
      <gz-text fontSize="10" fontWeight="400">${this.renderDaysText()}</gz-text>`;
  }

  private renderDaysElement(): TemplateResult | undefined {
    if (parseInt(this.days, 10) < 1) {
      return html`<gz-text fontSize="24" fontWeight="500" id="days">1</gz-text>`;
    }

    return html`<gz-text fontSize="24" fontWeight="500" id="days">${this.days}</gz-text>`;
  }

  private renderDaysText(): string {
    if (parseInt(this.days, 10) < 2) {
      return this.translations.day;
    }

    return this.translations.days;
  }

  private renderTitleText(): string {
    if (parseInt(this.days, 10) < 1) {
      return this.translations.titleLess;
    }

    return this.translations.titleMore;
  }

  private renderDays(): TemplateResult | undefined {
    if (parseInt(this.days, 10) < 1) {
      return;
    }

    return html` <div id="days">${this.days}d</div> `;
  }

  private renderHours(): TemplateResult | undefined {
    if (parseInt(this.hours, 10) < 1 && parseInt(this.days, 10) < 1) {
      return;
    }

    return html` <div id="hours">${this.hours}h</div> `;
  }

  private updateTimeValues(countDownDate: number) {
    const now = new Date().getTime();
    const formattedCountDownDate = new Date(countDownDate * 1000).getTime();
    const timeLeft = formattedCountDownDate - now;
    this.startedTimer = true;

    this.days = `${Math.floor(timeLeft / (1000 * 60 * 60 * 24))}`;

    this.hours = `${Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))}`;

    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    this.minutes = minutes >= 10 ? minutes.toString() : `0${minutes}`;

    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
    this.seconds = seconds >= 10 ? seconds.toString() : `0${seconds}`;

    const timeToSeconds = Math.floor(timeLeft / 1000);
    this.notifyReachedMilestones(timeToSeconds);

    if (timeLeft <= 0) {
      this.stopTimer();
    }
  }

  private notifyReachedMilestones(timeLeft: number) {
    this.milestones.forEach((milestone, index) => {
      if (timeLeft === milestone) {
        this.emitMilestoneReachedEvent(index);
      }
    });
  }

  private emitMilestoneReachedEvent(milestoneKey: number) {
    const event = new CustomEvent(GzCountDownEvents.TIMER_MILESTONE_REACHED, {
      detail: {
        timerId: this.getAttribute('id'),
        milestoneKey: milestoneKey,
        milestoneValue: this.milestones[milestoneKey],
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  startTimer() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    this.interval = setInterval(() => {
      this.updateTimeValues(this.dateToTimestamp);
    }, 1000);

    const event = new CustomEvent(GzCountDownEvents.TIMER_HAS_STARTED, {
      detail: {
        timerId: this.getAttribute('id'),
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }
  stopTimer() {
    if (this.interval) {
      clearInterval(this.interval);
    }

    this.days = '0';
    this.hours = '0';
    this.minutes = '00';
    this.seconds = '00';

    const event = new CustomEvent(GzCountDownEvents.TIMER_HAS_STOPPED, {
      detail: {
        timerId: this.getAttribute('id'),
      },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }
}
